import { useCallback, useEffect, useState } from 'react';
import { LOADING_TIMEOUT_MS } from '../constants/bookingConst';

/**
 * Reports when a loading state has been running for longer than it should,
 * so a screen can swap its spinner for ConnectionErrorScreen instead of
 * spinning forever.
 *
 * Why a timeout at all: every loading state in this app is fed by a Firebase
 * read or subscription, and neither reports "unreachable" on a dead or
 * captive-portal connection — the callback simply never fires. Without this,
 * a guest on hotel wifi sits on "Preparing your experience" indefinitely with
 * nothing to act on.
 *
 * `timedOut` is STICKY: once it fires, only a brand-new load or an explicit
 * `restart()` clears it. It deliberately does NOT clear when `loading` goes
 * false, because a stalled read that finally lands 30 seconds late would
 * otherwise yank the error screen away and drop the guest somewhere they
 * never asked to be — they have been told the connection failed, so nothing
 * moves until they choose to retry.
 *
 * `restart` is what a Try Again button calls. It clears the flag AND starts a
 * fresh countdown, which is why the internal `attempt` counter exists: a
 * retry that re-subscribes to the same Firebase node leaves `loading` true
 * the whole time, so the effect below would not otherwise re-run and the
 * screen would flip straight back to the error.
 *
 * @param {boolean} loading - the caller's own loading flag.
 * @param {object} [options]
 * @param {number} [options.timeoutMs=LOADING_TIMEOUT_MS] - override the
 *   configured timeout for one call site (e.g. a known-slow export query).
 * @param {boolean} [options.enabled=true] - set false to switch the watchdog
 *   off entirely, for a screen that should never time out.
 * @returns {{ timedOut: boolean, restart: () => void }}
 */
export const useLoadingTimeout = (loading, options = {}) => {
  const { timeoutMs = LOADING_TIMEOUT_MS, enabled = true } = options;
  const [timedOut, setTimedOut] = useState(false);
  const [attempt, setAttempt] = useState(0);

  const restart = useCallback(() => {
    setTimedOut(false);
    setAttempt((n) => n + 1);
  }, []);

  useEffect(() => {
    // Nothing in flight — leave any previous verdict STANDING. Clearing here
    // is what would let a late-arriving read replace the error screen with
    // content the guest never asked for (see the note above).
    if (!enabled || !loading) return undefined;

    // A new load is starting: this is the one moment the old verdict is
    // genuinely stale, so clear it and time the fresh attempt.
    setTimedOut(false);
    const timer = setTimeout(() => setTimedOut(true), timeoutMs);
    return () => clearTimeout(timer);
    // `attempt` is a dependency the body deliberately does not read: it is
    // here only so restart() re-runs this effect and starts a fresh timer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, enabled, timeoutMs, attempt]);

  return { timedOut, restart };
};

export default useLoadingTimeout;
